/**
 * Deterministic procedural weather readers.
 *
 * Weather data stores only RGBA8 cell coordinates. The readers generate the
 * animated center/color/scale from the frozen cell, time and explicit uniforms.
 * The implementation uses the engine Z-up convention: XY are horizontal and Z
 * is vertical.
 */
export const GSPLAT_WEATHER_READ_GLSL = /* glsl */ `
uniform float uWeatherTime;
uniform vec3 uWeatherBaseCell;
uniform float uWeatherCellSize;
uniform vec3 uWeatherGridHalf;
uniform float uWeatherSpeed;
uniform float uWeatherOffset;
uniform float uWeatherFall;
uniform float uWeatherWrap;
uniform float uWeatherDrift;
uniform float uWeatherFade;
uniform float uWeatherSize;
uniform float uWeatherOpacity;
uniform float uWeatherSeed;
uniform float uWeatherElongation;

vec3 gsplatWeatherHash(vec3 cell) {
    vec3 value = fract(cell * 0.1031 + uWeatherSeed);
    value += dot(value, value.yzx + 33.33);
    return fract((value.xxy + value.yzz) * value.zyx);
}

vec3 gsplatWeatherCell() {
    return floor(loadDataColor().rgb * 255.0 + 0.5);
}

vec3 getCenter() {
    vec3 cell = gsplatWeatherCell();
    vec3 worldCell = cell + uWeatherBaseCell - uWeatherGridHalf;
    vec3 hash = gsplatWeatherHash(worldCell);
    float phase = fract(hash.x + uWeatherOffset);
    float fall = fract(uWeatherTime * uWeatherSpeed + phase);
    float wrapped = mix(fall, fract(fall + uWeatherWrap), step(0.5, uWeatherWrap));
    vec3 center = (worldCell + vec3(0.5)) * uWeatherCellSize;
    center.z -= wrapped * uWeatherFall * uWeatherCellSize;
    center.xy += (hash.yz - 0.5) * uWeatherDrift * uWeatherCellSize;
    return center;
}

vec4 getColor() {
    vec3 hash = gsplatWeatherHash(gsplatWeatherCell());
    float alpha = uWeatherOpacity * (1.0 - uWeatherFade * hash.z);
    return vec4(hash.x, hash.y, 1.0 - hash.x, clamp(alpha, 0.0, 1.0));
}

vec3 getScale() {
    vec3 hash = gsplatWeatherHash(gsplatWeatherCell());
    float size = max(0.0001, uWeatherSize * (0.75 + hash.x * 0.5));
    return vec3(size, size, size * max(1.0, uWeatherElongation));
}

vec4 getRotation() {
    return vec4(0.0, 0.0, 0.0, 1.0);
}
`

export const GSPLAT_WEATHER_READ_WGSL = /* wgsl */ `
fn gsplatWeatherHash(cell: vec3f) -> vec3f {
    var value = fract(cell * 0.1031 + uniform.uWeatherSeed);
    value += dot(value, value.yzx + 33.33);
    return fract((value.xxy + value.yzz) * value.zyx);
}

fn gsplatWeatherCell() -> vec3f {
    return floor(loadDataColor().rgb * 255.0 + 0.5);
}

fn getCenter() -> vec3f {
    let cell = gsplatWeatherCell();
    let worldCell = cell + uniform.uWeatherBaseCell - uniform.uWeatherGridHalf;
    let hash = gsplatWeatherHash(worldCell);
    let phase = fract(hash.x + uniform.uWeatherOffset);
    let fall = fract(uniform.uWeatherTime * uniform.uWeatherSpeed + phase);
    let wrapped = mix(fall, fract(fall + uniform.uWeatherWrap), step(0.5, uniform.uWeatherWrap));
    var center = (worldCell + vec3f(0.5)) * uniform.uWeatherCellSize;
    center.z -= wrapped * uniform.uWeatherFall * uniform.uWeatherCellSize;
    // WGSL 不允许 swizzle 作为赋值左值，水平漂移必须逐分量累加。
    let drift = (hash.yz - 0.5) * uniform.uWeatherDrift * uniform.uWeatherCellSize;
    center.x += drift.x;
    center.y += drift.y;
    return center;
}

fn getColor() -> vec4f {
    let hash = gsplatWeatherHash(gsplatWeatherCell());
    let alpha = uniform.uWeatherOpacity * (1.0 - uniform.uWeatherFade * hash.z);
    return vec4f(hash.x, hash.y, 1.0 - hash.x, clamp(alpha, 0.0, 1.0));
}

fn getScale() -> vec3f {
    let hash = gsplatWeatherHash(gsplatWeatherCell());
    let size = max(0.0001, uniform.uWeatherSize * (0.75 + hash.x * 0.5));
    return vec3f(size, size, size * max(1.0, uniform.uWeatherElongation));
}

fn getRotation() -> vec4f {
    return vec4f(0.0, 0.0, 0.0, 1.0);
}
`
